import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateImportOrderDTO,
  ImportOrderListQueryDTO,
  ImportOrderTrackingInputDTO,
  InTransitReportQueryDTO,
  LandedCostPreviewInputDTO,
  MarkImportOrderAtCarrierDTO,
  MarkImportOrderShortDTO,
  MatchImportOrderLineDTO,
  ReceiveImportOrderDTO,
  SkipImportOrderLineDTO,
  UpdateImportOrderDTO,
  UpdateImportOrderTrackingDTO,
} from "@/modules/inventory/domain/import-order-contracts";
import { importOrdersClient } from "./import-orders-client";

export const importOrderQueryKeys = {
  all: ["import-orders"] as const,
  list: (query?: ImportOrderListQueryDTO) =>
    [...importOrderQueryKeys.all, "list", query] as const,
  detail: (id: string) => [...importOrderQueryKeys.all, "detail", id] as const,
  landedCostPreview: (id: string) =>
    [...importOrderQueryKeys.all, "landed-cost-preview", id] as const,
  summary: () => [...importOrderQueryKeys.all, "summary"] as const,
  overdue: () => [...importOrderQueryKeys.all, "overdue"] as const,
  incoming: (variantIds: string[]) =>
    [...importOrderQueryKeys.all, "incoming", variantIds] as const,
  exchangeSettings: () =>
    [...importOrderQueryKeys.all, "exchange-settings"] as const,
  arrivalEstimates: () =>
    [...importOrderQueryKeys.all, "arrival-estimates"] as const,
  inTransitReport: (
    kind: "order" | "variant",
    query?: InTransitReportQueryDTO,
  ) => [...importOrderQueryKeys.all, "in-transit-report", kind, query] as const,
  priceHistory: (variantId: string) =>
    [...importOrderQueryKeys.all, "price-history", variantId] as const,
};

function invalidateImportOrderQueries(
  qc: ReturnType<typeof useQueryClient>,
  id?: string,
) {
  qc.invalidateQueries({ queryKey: importOrderQueryKeys.all });
  if (id) {
    qc.invalidateQueries({ queryKey: importOrderQueryKeys.detail(id) });
  }
  qc.invalidateQueries({ queryKey: ["inventory"] });
}

export function useImportOrders(query?: ImportOrderListQueryDTO) {
  return useQuery({
    queryKey: importOrderQueryKeys.list(query),
    queryFn: () => importOrdersClient.list(query),
  });
}

export function useImportOrder(id: string) {
  return useQuery({
    queryKey: importOrderQueryKeys.detail(id),
    queryFn: () => importOrdersClient.detail(id),
    enabled: Boolean(id),
  });
}

export function useImportOrderLandedCostPreview(id: string) {
  return useQuery({
    queryKey: importOrderQueryKeys.landedCostPreview(id),
    queryFn: () => importOrdersClient.landedCostPreviewById(id),
    enabled: Boolean(id),
  });
}

export function usePreviewImportOrderLandedCost() {
  return useMutation({
    mutationFn: (input: LandedCostPreviewInputDTO) =>
      importOrdersClient.previewLandedCost(input),
  });
}

export function useCreateImportOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateImportOrderDTO) =>
      importOrdersClient.create(input),
    onSuccess: () => invalidateImportOrderQueries(qc),
  });
}

export function useUpdateImportOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateImportOrderDTO) =>
      importOrdersClient.update(id, input),
    onSuccess: () => invalidateImportOrderQueries(qc, id),
  });
}

export function useMarkImportOrderOrdered() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => importOrdersClient.markOrdered(id),
    onSuccess: (_data, id) => invalidateImportOrderQueries(qc, id),
  });
}

export function useMarkImportOrderAtCarrier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: MarkImportOrderAtCarrierDTO }) =>
      importOrdersClient.markAtCarrier(input.id, input.body),
    onSuccess: (_data, input) => invalidateImportOrderQueries(qc, input.id),
  });
}

export function useAddImportOrderTracking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      tracking: ImportOrderTrackingInputDTO;
    }) => importOrdersClient.addTracking(input.id, input.tracking),
    onSuccess: (_data, input) => invalidateImportOrderQueries(qc, input.id),
  });
}

export function useUpdateImportOrderTracking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      trackingId: string;
      patch: UpdateImportOrderTrackingDTO;
    }) =>
      importOrdersClient.updateTracking(
        input.id,
        input.trackingId,
        input.patch,
      ),
    onSuccess: (_data, input) => invalidateImportOrderQueries(qc, input.id),
  });
}

export function useReceiveImportOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: ReceiveImportOrderDTO }) =>
      importOrdersClient.receive(input.id, input.body),
    onSuccess: (_data, input) => {
      invalidateImportOrderQueries(qc, input.id);
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useVoidImportOrderReceive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; receiptId: string }) =>
      importOrdersClient.voidReceive(input.id, input.receiptId),
    onSuccess: (_data, input) => {
      invalidateImportOrderQueries(qc, input.id);
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useVoidImportOrderGoodsReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; goodsReceiptId: string }) =>
      importOrdersClient.voidGoodsReceipt(input.id, input.goodsReceiptId),
    onSuccess: (_data, input) => {
      invalidateImportOrderQueries(qc, input.id);
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useReverseLegacyImportOrderReceive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => importOrdersClient.reverseReceive(id),
    onSuccess: (_data, id) => {
      invalidateImportOrderQueries(qc, id);
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useMarkImportOrderShort() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: MarkImportOrderShortDTO }) =>
      importOrdersClient.markShort(input.id, input.body),
    onSuccess: (_data, input) => invalidateImportOrderQueries(qc, input.id),
  });
}

export function useCancelImportOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => importOrdersClient.cancel(id),
    onSuccess: (_data, id) => invalidateImportOrderQueries(qc, id),
  });
}

export function useImportOrderSummary() {
  return useQuery({
    queryKey: importOrderQueryKeys.summary(),
    queryFn: () => importOrdersClient.summary(),
  });
}

export function useOverdueImportOrders() {
  return useQuery({
    queryKey: importOrderQueryKeys.overdue(),
    queryFn: () => importOrdersClient.overdue(),
  });
}

export function useVariantIncomingStock(variantIds: string[]) {
  const sortedKey = [...variantIds].sort().join(",");
  return useQuery({
    queryKey: importOrderQueryKeys.incoming(
      sortedKey ? sortedKey.split(",") : [],
    ),
    queryFn: () => importOrdersClient.incomingByVariants(variantIds),
    enabled: variantIds.length > 0,
  });
}

export function useImportOrderExchangeSettings() {
  return useQuery({
    queryKey: importOrderQueryKeys.exchangeSettings(),
    queryFn: () => importOrdersClient.exchangeSettings(),
  });
}

export function useImportArrivalEstimates() {
  return useQuery({
    queryKey: importOrderQueryKeys.arrivalEstimates(),
    queryFn: () => importOrdersClient.arrivalEstimates(),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateImportOrderExchangeSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { defaultExchangeRate: number }) =>
      importOrdersClient.updateExchangeSettings(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: importOrderQueryKeys.exchangeSettings(),
      });
    },
  });
}

export function useInTransitReportByOrder(query?: InTransitReportQueryDTO) {
  return useQuery({
    queryKey: importOrderQueryKeys.inTransitReport("order", query),
    queryFn: () => importOrdersClient.inTransitByOrder(query),
  });
}

export function useInTransitReportByVariant(query?: InTransitReportQueryDTO) {
  return useQuery({
    queryKey: importOrderQueryKeys.inTransitReport("variant", query),
    queryFn: () => importOrdersClient.inTransitByVariant(query),
  });
}

export function useVariantImportPriceHistory(variantId: string) {
  return useQuery({
    queryKey: importOrderQueryKeys.priceHistory(variantId),
    queryFn: () => importOrdersClient.variantPriceHistory(variantId),
    enabled: Boolean(variantId),
  });
}

export function useMatchImportOrderLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      importOrderId: string;
      lineId: string;
      body: MatchImportOrderLineDTO;
    }) =>
      importOrdersClient.matchLine(
        input.importOrderId,
        input.lineId,
        input.body,
      ),
    onSuccess: (_data, input) => {
      invalidateImportOrderQueries(qc, input.importOrderId);
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useSkipImportOrderLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      importOrderId: string;
      lineId: string;
      body: SkipImportOrderLineDTO;
    }) =>
      importOrdersClient.skipLine(
        input.importOrderId,
        input.lineId,
        input.body,
      ),
    onSuccess: (_data, input) =>
      invalidateImportOrderQueries(qc, input.importOrderId),
  });
}
